import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LeadsLayout } from '@/components/LeadsLayout';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#EF4444', '#F97316', '#F59E0B', '#84CC16', '#22C55E', '#14B8A6', '#3B82F6', '#8B5CF6'];

// Date format helper - DD-MM-YYYY
const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

// Get first day of current month in YYYY-MM-DD format (timezone safe)
const getFirstDayOfMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

// Get today in YYYY-MM-DD format (timezone safe)
const getToday = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Generate avatar color from name
const getAvatarColor = (name) => {
  const colors = [
    'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 
    'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-orange-500'
  ];
  const index = name ? name.charCodeAt(0) % colors.length : 0;
  return colors[index];
};

function LostLeadReport({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [reasonsData, setReasonsData] = React.useState([]);
  const [lostLeads, setLostLeads] = React.useState([]);
  const [dateRange, setDateRange] = React.useState({
    from: getFirstDayOfMonth(),
    to: getToday()
  });

  React.useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.append('date_from', dateRange.from);
      if (dateRange.to) params.append('date_to', dateRange.to);
      
      const [reasonsRes, leadsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/leads/analytics/lost-reasons?${params.toString()}`),
        axios.get(`${BACKEND_URL}/api/leads?stage=lost&${params.toString()}`)
      ]);
      
      setReasonsData(reasonsRes.data);
      setLostLeads(leadsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load report data');
    }
    setLoading(false);
  };

  // Prepare chart data
  const pieData = reasonsData.map((item, idx) => ({
    name: item.reason,
    value: item.count,
    color: COLORS[idx % COLORS.length]
  }));

  const totalLost = reasonsData.reduce((acc, item) => acc + item.count, 0);

  // Group by competitor
  const competitorData = React.useMemo(() => {
    const competitors = {};
    lostLeads.forEach(lead => {
      if (lead.lost_to_competitor) {
        competitors[lead.lost_to_competitor] = (competitors[lead.lost_to_competitor] || 0) + 1;
      }
    });
    return Object.entries(competitors)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [lostLeads]);

  const formatDate = (dateStr) => {
    return formatDateDDMMYYYY(dateStr);
  };

  // Export to Excel
  const exportToExcel = () => {
    const exportData = lostLeads.map(lead => ({
      'Customer Name': `${lead.first_name} ${lead.last_name || ''}`,
      'Mobile': lead.mobile,
      'Model': lead.interested_model || '-',
      'Lost Reason': lead.lost_reason || '-',
      'Lost To': lead.lost_to_competitor || '-',
      'Sales Rep': lead.assigned_to || '-',
      'Lost Date': formatDateDDMMYYYY(lead.updated_at)
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lost Leads');
    XLSX.writeFile(wb, `Lost_Lead_Report_${dateRange.from}_to_${dateRange.to}.xlsx`);
    toast.success('Report exported to Excel');
  };

  return (
    <LeadsLayout user={user} onLogout={onLogout} title="Lost Lead Analysis">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800" data-testid="report-title">Lost Lead Analysis</h2>
            <p className="text-sm text-gray-500">Understand why leads are being lost • <span className="font-medium text-gray-700">{totalLost} lost leads</span></p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">From:</label>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                data-testid="date-from"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">To:</label>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                data-testid="date-to"
              />
            </div>
            <div className="relative group">
              <button className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm">
                📥 Export
              </button>
              <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border hidden group-hover:block z-10">
                <button onClick={exportToExcel} className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100">📊 Export to Excel</button>
                <button onClick={() => window.print()} className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100">📄 Print/PDF</button>
              </div>
            </div>
            <button
              onClick={() => navigate('/leads')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              ← Back
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          </div>
        ) : (
          <>
            {/* Summary Card */}
            <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100">Total Lost Leads</p>
                  <p className="text-5xl font-bold">{totalLost}</p>
                  <p className="text-red-200 mt-2">In selected period</p>
                </div>
                <div className="text-6xl opacity-50">📉</div>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Pie Chart - Reasons */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Loss Reasons Distribution</h3>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-400">
                    No data available
                  </div>
                )}
              </div>

              {/* Bar Chart - Competitors */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Lost to Competitors</h3>
                {competitorData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={competitorData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#EF4444" name="Leads Lost" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-400">
                    No competitor data available
                  </div>
                )}
              </div>
            </div>

            {/* Reasons Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-800">Reasons Breakdown</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="reasons-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Count</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Percentage</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Impact</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {reasonsData.map((item, idx) => (
                      <tr key={item.reason} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                            <span className="font-medium text-gray-800">{item.reason}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full font-medium">{item.count}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="font-medium">{item.percentage}%</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-red-500 h-2 rounded-full" 
                              style={{ width: `${item.percentage}%` }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Lost Leads List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-800">Recently Lost Leads ({lostLeads.length})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interest</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lost Reason</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lost To</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {lostLeads.slice(0, 20).map(lead => {
                      const avatarColor = getAvatarColor(lead.first_name);
                      return (
                        <tr key={lead.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${avatarColor}`}>
                                {lead.first_name?.[0]?.toUpperCase() || '?'}
                                {lead.last_name?.[0]?.toUpperCase() || ''}
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{lead.first_name} {lead.last_name || ''}</p>
                                <p className="text-xs text-gray-500">#{lead.lead_no} • {lead.mobile}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {lead.interested_model || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {lead.lead_source}
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm">
                              {lead.lost_reason || 'Not Specified'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {lead.lost_to_competitor || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {lead.assigned_to || 'Unassigned'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </LeadsLayout>
  );
}

export default LostLeadReport;
